import { useState, useEffect } from 'react';
import { mockPersonal } from '../../../mocks/personal';

interface AttendanceRecord {
  id: string;
  personalId: string;
  personalName: string;
  date: string;
  checkIn: string;
  checkOut: string | null;
  status: 'active' | 'completed';
  checkInLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  checkOutLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
}

interface LocationData {
  latitude: number;
  longitude: number;
  address: string;
}

export default function AttendanceControl() {
  const [selectedPersonal, setSelectedPersonal] = useState('');
  const [activeRecords, setActiveRecords] = useState<AttendanceRecord[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(false);

  useEffect(() => {
    if ('geolocation' in navigator) {
      setLocationEnabled(true);
    }

    const mockActiveRecords: AttendanceRecord[] = [
      {
        id: '1',
        personalId: '1',
        personalName: 'Carlos Rodríguez',
        date: new Date().toISOString().split('T')[0],
        checkIn: '08:30',
        checkOut: null,
        status: 'active',
        checkInLocation: {
          latitude: 25.7617,
          longitude: -80.1918,
          address: 'Restaurante Principal - 123 Main St, Miami, FL'
        }
      },
      {
        id: '2',
        personalId: '2',
        personalName: 'María González',
        date: new Date().toISOString().split('T')[0],
        checkIn: '08:45',
        checkOut: null,
        status: 'active',
        checkInLocation: {
          latitude: 25.7617,
          longitude: -80.1918,
          address: 'Restaurante Principal - 123 Main St, Miami, FL'
        }
      }
    ];
    setActiveRecords(mockActiveRecords);
  }, []);

  const getCurrentLocation = (): Promise<LocationData> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocalización no disponible'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          try {
            const mockAddress = 'Restaurante Principal - 123 Main St, Miami, FL';
            resolve({
              latitude,
              longitude,
              address: mockAddress
            });
          } catch (error) {
            resolve({
              latitude,
              longitude,
              address: `Lat: ${latitude.toFixed(4)}, Lng: ${longitude.toFixed(4)}`
            });
          }
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    });
  };

  const handleCheckIn = async () => {
    if (!selectedPersonal) {
      setSuccessMessage('Por favor selecciona un miembro del personal');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      return;
    }

    const personal = mockPersonal.find(p => p.id === selectedPersonal);
    if (!personal) return;

    setLocationLoading(true);
    let location: LocationData | undefined;

    if (locationEnabled) {
      try {
        location = await getCurrentLocation();
      } catch (error) {
        console.error('Error obteniendo ubicación:', error);
      }
    }

    const now = new Date();
    const newRecord: AttendanceRecord = {
      id: Date.now().toString(),
      personalId: personal.id,
      personalName: personal.name,
      date: now.toISOString().split('T')[0],
      checkIn: now.toTimeString().slice(0, 5),
      checkOut: null,
      status: 'active',
      checkInLocation: location
    };

    setActiveRecords(prev => [...prev, newRecord]);
    setSelectedPersonal('');
    
    const locationText = location 
      ? ` desde ${location.address}`
      : ' (sin ubicación)';
    
    setSuccessMessage(`Entrada registrada: ${personal.name} a las ${newRecord.checkIn}${locationText}`);
    setShowSuccess(true);
    setLocationLoading(false);
    setTimeout(() => setShowSuccess(false), 5000);
  };

  const handleCheckOut = async (recordId: string) => {
    const record = activeRecords.find(r => r.id === recordId);
    if (!record) return;

    setLocationLoading(true);
    let location: LocationData | undefined;

    if (locationEnabled) {
      try {
        location = await getCurrentLocation();
      } catch (error) {
        console.error('Error obteniendo ubicación:', error);
      }
    }

    const now = new Date();
    const checkOutTime = now.toTimeString().slice(0, 5);

    const locationText = location 
      ? ` desde ${location.address}`
      : ' (sin ubicación)';
    
    setSuccessMessage(`Salida registrada: ${record.personalName} a las ${checkOutTime}${locationText}`);
    setShowSuccess(true);
    
    setTimeout(() => {
      setActiveRecords(prev => prev.map(r => {
        if (r.id === recordId) {
          return { 
            ...r, 
            checkOut: checkOutTime, 
            status: 'completed' as const,
            checkOutLocation: location
          };
        }
        return r;
      }));
      setLocationLoading(false);
    }, 100);

    setTimeout(() => setShowSuccess(false), 5000);
  };

  const calculateHours = (checkIn: string, checkOut: string | null) => {
    if (!checkOut) return 'En turno';
    
    const [inHour, inMin] = checkIn.split(':').map(Number);
    const [outHour, outMin] = checkOut.split(':').map(Number);
    
    const totalMinutes = (outHour * 60 + outMin) - (inHour * 60 + inMin);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  const getLocationIcon = (hasLocation: boolean) => {
    return hasLocation ? 'ri-map-pin-line text-green-600' : 'ri-map-pin-line text-gray-400';
  };

  const activeStaff = activeRecords.filter(r => r.status === 'active');
  const completedStaff = activeRecords.filter(r => r.status === 'completed');

  return (
    <div className="space-y-8">
      {showSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-lg flex items-center gap-3">
          <i className="ri-checkbox-circle-line text-2xl"></i>
          <span className="font-semibold">{successMessage}</span>
        </div>
      )}

      {!locationEnabled && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-6 py-4 rounded-lg flex items-center gap-3">
          <i className="ri-alert-line text-2xl"></i>
          <div>
            <span className="font-semibold">Ubicación deshabilitada</span>
            <p className="text-sm mt-1">Los registros no incluirán información de ubicación. Habilita la geolocalización para mayor control.</p>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 border border-amber-100">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-700 rounded-full flex items-center justify-center">
            <i className="ri-login-box-line text-white text-xl"></i>
          </div>
          Registrar Entrada
          {locationEnabled && (
            <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
              <i className="ri-map-pin-line mr-1"></i>
              Ubicación activa
            </span>
          )}
        </h3>

        <div className="flex gap-4">
          <select
            value={selectedPersonal}
            onChange={(e) => setSelectedPersonal(e.target.value)}
            className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all cursor-pointer text-base"
          >
            <option value="">Selecciona un miembro del personal</option>
            {mockPersonal.map(person => (
              <option key={person.id} value={person.id}>
                {person.name} - {person.position}
              </option>
            ))}
          </select>

          <button
            onClick={handleCheckIn}
            disabled={locationLoading}
            className="px-8 py-3 bg-amber-700 text-white rounded-lg font-semibold hover:bg-amber-800 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {locationLoading ? (
              <>
                <i className="ri-loader-line mr-2 animate-spin"></i>
                Obteniendo ubicación...
              </>
            ) : (
              <>
                <i className="ri-login-box-line mr-2"></i>
                Registrar Entrada
              </>
            )}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
            <i className="ri-user-follow-line text-white text-xl"></i>
          </div>
          Personal en Turno Actual
        </h3>

        {activeStaff.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-12 text-center">
            <i className="ri-user-unfollow-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">No hay personal en turno actualmente</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeStaff.map(record => (
              <div key={record.id} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {record.personalName.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-lg">{record.personalName}</h4>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <i className="ri-login-box-line text-green-600"></i>
                        Entrada: {record.checkIn}
                      </p>
                      {record.checkInLocation && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <i className={getLocationIcon(!!record.checkInLocation)}></i>
                          {record.checkInLocation.address}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                    Activo
                  </span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-gray-600">
                    <i className="ri-time-line"></i>
                    <span className="text-sm font-semibold">{calculateHours(record.checkIn, null)}</span>
                  </div>
                  <button
                    onClick={() => handleCheckOut(record.id)}
                    disabled={locationLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {locationLoading ? (
                      <i className="ri-loader-line animate-spin"></i>
                    ) : (
                      <>
                        <i className="ri-logout-box-line mr-1"></i>
                        Registrar Salida
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {completedStaff.length > 0 && (
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-white text-xl"></i>
            </div>
            Turnos Completados Hoy
          </h3>

          <div className="space-y-3">
            {completedStaff.map(record => (
              <div key={record.id} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">
                        {record.personalName.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{record.personalName}</h4>
                      <p className="text-sm text-gray-600">
                        {record.checkIn} - {record.checkOut}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-1">Horas trabajadas</p>
                    <p className="text-xl font-bold text-gray-900">
                      {calculateHours(record.checkIn, record.checkOut)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                  {record.checkInLocation && (
                    <div className="flex items-start gap-2">
                      <i className="ri-login-box-line text-green-600 mt-1"></i>
                      <div>
                        <p className="text-xs font-semibold text-gray-700">Entrada desde:</p>
                        <p className="text-xs text-gray-500">{record.checkInLocation.address}</p>
                      </div>
                    </div>
                  )}
                  
                  {record.checkOutLocation && (
                    <div className="flex items-start gap-2">
                      <i className="ri-logout-box-line text-red-600 mt-1"></i>
                      <div>
                        <p className="text-xs font-semibold text-gray-700">Salida desde:</p>
                        <p className="text-xs text-gray-500">{record.checkOutLocation.address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
